/*
Developers: Sakshi Bhise
File: NoPage.tsx
Date: 06/18/2025
Project Name: AskHR
Team: DSET

Purpose:
This component displays a simple 404 error message for any route not found
within the AskHR portal. It helps handle undefined paths gracefully, improving
the user experience when navigating to incorrect or missing pages.
*/

// ======================== Imports ======================== //  
// Import React for component creation  
import React from 'react';  
// Import error images for desktop and mobile variants  
import ErrorDesktop from '../assets/Chat Warning Web.png';  
import ErrorMobile from '../assets/Chat Warning Mob.png'; // New mobile image  
// Import CSS module for layout styles  
import styles from "./layout/Layout.module.css";  
// Import AskHR logo for branding  
import AskHR from "../assets/AskHR w Teva logo.png";  
  
// ======================== Component ======================== //  
/**  
 * NoPage  
 * Displays a friendly message when AskHR AI is unavailable due to device, country, or group restriction.  
 *  
 * @param reason   - Why the page is blocked ('device', 'country', 'group')  
 * @param variant  - Whether the view is 'mobile' or 'desktop'  
 */  
const NoPage: React.FC<{ reason: 'device' | 'country' | 'group' | 'not_found'; variant?: 'mobile' | 'desktop' }> = ({  
  reason,  
  variant = 'desktop' // Default to desktop if not specified  
}) => {  
  // Detect if mobile variant is requested  
  const isMobile = variant === 'mobile';  
  
  // Set the appropriate message based on restriction reason  
  let message = '';  
  switch (reason) {  
    case 'group':
      message = 'AskHR AI is currently available only to Teva employees.';
      break;
    case 'not_found':
      message = 'Your account was not found. Please contact HR support.';
      break;
    default:  
      message = 'Currently, AskHR AI is accessible only for Teva employees in Israel and Germany, within the Teva network (mobile not supported). Please verify your VPN connection.';  
  }  
  
  // ======================== Render ======================== //  
  return (  
    <>  
      {/* ---------- Header Section (Logo) ---------- */}  
      <div className={styles.headerStackContainer}>  
        <div style={{  
          display: 'flex',  
          alignItems: 'center',  
          marginTop: "15px" // Spacing from the top for visual comfort  
        }}>  
          {/* Logo for brand recognition, decorative only */}  
          <img  
            src={AskHR}  
            style={{ height: '90px' }}    // Consistent logo size  
            aria-hidden="true"             // Not announced by screen readers  
          />  
        </div>  
      </div>  
  
      {/* ---------- Centered Error Message Section ---------- */}  
      <div  
        style={{  
          minHeight: isMobile ? '90vh' : '100vh', // Occupies full/most viewport height  
          display: 'flex',                        // Flex layout for easy centering  
          flexDirection: 'column',  
          alignItems: 'center',                   // Center horizontally  
          justifyContent: 'center',               // Center vertically  
          fontFamily: 'Segoe UI, sans-serif',     // Matches enterprise style  
          background: '#fff',                     // Clean white background  
          padding: isMobile ? '0 8vw' : '0'       // Extra horizontal space on mobile  
        }}  
      >  
        {/* ---------- Error Image ---------- */}  
        <img  
          src={isMobile ? ErrorMobile : ErrorDesktop} // Select correct image by device  
          alt='Chat logo'  
          style={{  
            marginBottom: isMobile ? 32 : 24,        // More space below image for mobile  
            maxWidth: isMobile ? '70vw' : undefined, // Limit width on mobile for better fit  
            objectFit: 'contain'                     // Prevents image distortion  
          }}  
        />  
  
        {/* ---------- Headline ---------- */}  
        <h2  
          style={{  
            fontWeight: 700,                        // Bold for emphasis  
            fontSize: isMobile ? 32 : 40,           // Slightly smaller on mobile  
            margin: 0,  
            marginBottom: isMobile ? 20 : 16,       // Extra space below headline on mobile  
            color: '#222'                           // Very dark gray for strong contrast  
          }}  
        >  
          Oops!!  
        </h2>  
  
        {/* ---------- Main Message ---------- */}  
        <div  
          style={{  
            fontSize: isMobile ? 20 : 32,           // Smaller font for mobile screens  
            color: '#333',                          // Dark gray for readability  
            textAlign: 'center',  
            maxWidth: isMobile ? 340 : 470,         // Prevents long lines, readable on all screens  
            margin: '0 auto',  
            lineHeight: 1.5                         // Comfortable line spacing  
          }}  
        >  
          {message}  
        </div>  
      </div>  
    </>  
  );  
};  
  
// Export the component for use in the application  
export default NoPage;  